import { cn } from "@norican/react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useElementWidth } from "@/hooks/use-element-width";
import { type EccLevel, encodeQr, type QrMatrix } from "@/lib/qr";

interface QrCodeProps {
  /** Text to encode — for a business card, the vCard. */
  value: string;
  /** An already encoded symbol; when given, `value`/`ecc` are not encoded again. */
  matrix?: QrMatrix;
  /** Error-correction level; M survives a scuffed phone screen, H a printed card. */
  ecc?: EccLevel;
  /** Light margin around the symbol, in modules (the standard asks for four). */
  quietZone?: number;
  /**
   * The edge the symbol should be drawn at, in css px (it is then snapped
   * down to whole device pixels per module). When omitted the symbol fills
   * the width of its box; the box must then not take its width from the
   * symbol, or the measurement is circular.
   */
  edge?: number;
  /**
   * Smallest module edge in css px. The symbol never renders below it even
   * when `edge` or the box is narrower; the box then has to grow around it.
   */
  minModulePx?: number;
  /** Accessible name for the symbol. */
  label?: string;
  /** Classes for the box around the symbol. */
  className?: string;
  /** Classes for the symbol itself, e.g. `ml-auto` to right-align it in its box. */
  symbolClassName?: string;
}

/**
 * Renders a QR code as inline SVG using the in-repo encoder (`@/lib/qr`).
 *
 * Dark modules use `currentColor`, so the colour follows the text colour of
 * the surrounding element (`text-brand-blue-700` for Norican Blue). The
 * light background is the SVG's own white rect, which doubles as the quiet
 * zone when `quietZone` is set, so the symbol scans no matter what it sits on.
 *
 * Module edges land on whole device pixels: the symbol is drawn at the
 * largest size not above `edge` (or the box width) that gives every module
 * the same integer number of device pixels (css px × devicePixelRatio). A
 * symbol with mixed 3 px and 4 px modules is measurably harder to decode
 * than one with uniform 3 px modules, and at the sizes a card allows that
 * margin matters.
 */
export function QrCode({
  value,
  matrix,
  ecc = "M",
  quietZone = 4,
  edge,
  minModulePx = 0,
  label = "QR code",
  className,
  symbolClassName,
}: QrCodeProps) {
  const { path, total } = useMemo(() => {
    const symbol = matrix ?? encodeQr(value, ecc);
    const side = symbol.size + quietZone * 2;
    let d = "";
    for (let y = 0; y < symbol.size; y++) {
      for (let x = 0; x < symbol.size; x++) {
        if (symbol.modules[y]?.[x]) {
          d += `M${x + quietZone} ${y + quietZone}h1v1h-1z`;
        }
      }
    }
    return { path: d, total: side };
  }, [value, matrix, ecc, quietZone]);

  const boxRef = useRef<HTMLDivElement>(null);
  const boxWidth = useElementWidth(boxRef);
  const dpr = useDevicePixelRatio();
  const available = edge ?? boxWidth;
  const snapped = useMemo(() => {
    if (available === null || available === undefined) {
      return null;
    }
    const devicePx = Math.max(Math.floor((available * dpr) / total), Math.ceil(minModulePx * dpr));
    return devicePx > 0 ? (devicePx * total) / dpr : null;
  }, [available, dpr, total, minModulePx]);

  return (
    <div ref={boxRef} className={cn("w-full", className)}>
      <svg
        viewBox={`0 0 ${total} ${total}`}
        width={snapped ?? undefined}
        height={snapped ?? undefined}
        role="img"
        aria-label={label}
        shapeRendering="crispEdges"
        className={cn("block", snapped === null && "h-auto w-full", symbolClassName)}
      >
        <rect width={total} height={total} fill="#ffffff" />
        <path d={path} fill="currentColor" />
      </svg>
    </div>
  );
}

/** `window.devicePixelRatio`, re-read when the browser zoom changes it. */
function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(() =>
    typeof window === "undefined" ? 1 : window.devicePixelRatio || 1,
  );
  useLayoutEffect(() => {
    const query = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const update = () => setDpr(window.devicePixelRatio || 1);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [dpr]);
  return dpr;
}
